import qs from 'qs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteTankApi, getAllTanksApi, getTankApi, getTanksApi } from '@/apis/tanks'
import { API_LIMIT } from '@/constants/api'
import useNotification from '@/hooks/useNotification'
import useQuery from '@/hooks/useQuery'
import useQueryPage from '@/hooks/useQueryPage'
import { Tank } from '@/types'
import { DeleteIdCardRequest } from '@/types/apis/idCards'
import { GetAllTanksResponse } from '@/types/apis/tanks/getAllTankResponse'

export type PageQueryParams = {
  page?: number
}

export const useTanks = () => {
  const { t } = useTranslation()
  const openNotification = useNotification()
  const currentPage = useQueryPage()
  const query = useQuery()
  const [page, setPage] = useState(currentPage)
  const [getTanksLoading, setGetTanksLoading] = useState(false)
  const [getTanksResponse, setTanksResponse] = useState<GetAllTanksResponse>()
  const [getTankDetailResponse, setTankDetailResponse] = useState<Tank>()
  const [deleteTankLoading, setTankDetergentLoading] = useState(false)
  const [tanks, setTanks] = useState<Tank[]>()

  const filterToQuery = (filter: { page?: number }): string => {
    const filterQuery: PageQueryParams = {}

    if (filter.page) {
      filterQuery.page = filter.page
    }

    return qs.stringify(filterQuery)
  }

  const get = useCallback(async () => {
    try {
      setGetTanksLoading(true)
      const res = await getAllTanksApi({ limit: API_LIMIT, page })

      setTanksResponse(res)
    } finally {
      setGetTanksLoading(false)
    }
  }, [page])

  const getTanks = useCallback(async () => {
    try {
      setGetTanksLoading(true)
      const res = await getTanksApi()

      setTanks(res)
    } finally {
      setGetTanksLoading(false)
    }
  }, [])

  const getById = useCallback(async (id: number) => {
    try {
      setGetTanksLoading(true)
      const res = await getTankApi({ id })

      setTankDetailResponse(res)
    } finally {
      setGetTanksLoading(false)
    }
  }, [])

  const del = useCallback(
    async ({ id }: DeleteIdCardRequest) => {
      try {
        setTankDetergentLoading(true)
        await deleteTankApi({ id })
        openNotification({ type: 'success', description: t('components.pages.admins.common.delete.success') })
      } catch {
        openNotification({ type: 'error', description: t('components.pages.admins.common.delete.fail') })
      } finally {
        setTankDetergentLoading(false)
      }
    },
    [openNotification, t]
  )

  useEffect(() => {
    const page = query.get('page')
    setPage(page ? Number(page) : 1)
  }, [query])

  return {
    page,
    pageSize: API_LIMIT,
    getTanks: get,
    getTanksResponse,
    getTanksLoading,
    deleteTank: del,
    deleteTankLoading,
    getTankDetail: getById,
    getTankDetailResponse,
    filterToQuery,
    getAllTanks: getTanks,
    tanks,
  }
}
